import * as React from 'react';
import {
  AlertCircle,
  CheckCircle2,
  Circle,
  Check,
  Eye,
  EyeOff,
  Info,
  Loader2,
} from 'lucide-react';
import { Button, type ButtonProps } from '@/components/ui/button';
import { Input, type InputProps } from '@/components/ui/input';
import { cn } from '@/lib/utils';

/** Shared building blocks for sign-in / register / reset (design auth-kit.js). */

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export const PASSWORD_RULES: { key: string; label: string; test: (v: string) => boolean }[] = [
  { key: 'len', label: 'At least 8 characters', test: (v) => v.length >= 8 },
  { key: 'upper', label: 'One uppercase letter', test: (v) => /[A-Z]/.test(v) },
  { key: 'lower', label: 'One lowercase letter', test: (v) => /[a-z]/.test(v) },
  { key: 'num', label: 'One number', test: (v) => /[0-9]/.test(v) },
];

export const passwordOk = (v: string) => PASSWORD_RULES.every((r) => r.test(v));

export const emailMsg = (v: string) =>
  !v.trim()
    ? 'Enter your email address.'
    : EMAIL_RE.test(v.trim())
      ? ''
      : 'Enter a valid email address, like name@company.com.';

export const passwordMsg = (v: string) =>
  !v ? 'Enter a password.' : passwordOk(v) ? '' : 'Password does not meet every requirement below.';

export const mmss = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

/** Card heading block: title + muted sub. */
export function CardIntro({
  title,
  sub,
  id,
  mark,
  eyebrow,
}: {
  title: React.ReactNode;
  sub?: React.ReactNode;
  id?: string;
  mark?: React.ReactNode;
  eyebrow?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col">
      {mark && <div className="mb-4">{mark}</div>}
      {eyebrow && (
        <p className="mb-2.5 font-mono text-[11px] uppercase tracking-[0.08em] text-muted-foreground">
          {eyebrow}
        </p>
      )}
      <h2 id={id} className="text-[22px] font-semibold leading-tight tracking-[-0.03em]">
        {title}
      </h2>
      {sub && <p className="mt-1.5 text-muted-foreground">{sub}</p>}
    </div>
  );
}

/** Large round status icon (design .status-mark). */
export function StatusMark({
  tone,
  children,
}: {
  tone: 'ok' | 'bad' | 'neutral';
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        'grid h-11 w-11 place-items-center rounded-full [&_svg]:h-5 [&_svg]:w-5',
        tone === 'ok' && 'bg-success/[0.12] text-success',
        tone === 'bad' && 'bg-destructive/[0.12] text-destructive',
        tone === 'neutral' && 'bg-foreground/[0.06] text-muted-foreground'
      )}
    >
      {children}
    </div>
  );
}

/** Form-level alert shown above the form. */
export function FormAlert({
  tone = 'bad',
  title,
  children,
}: {
  tone?: 'bad' | 'ok' | 'info';
  title: string;
  children?: React.ReactNode;
}) {
  const Icon = tone === 'bad' ? AlertCircle : tone === 'ok' ? CheckCircle2 : Info;
  return (
    <div
      role="alert"
      className={cn(
        'flex items-start gap-2.5 rounded-lg border p-3 text-[13px]',
        tone === 'bad' && 'border-destructive/40 bg-destructive/[0.06]',
        tone === 'ok' && 'border-success/40 bg-success/[0.06]',
        tone === 'info' && 'border-border bg-foreground/[0.03]'
      )}
    >
      <Icon
        className={cn(
          'mt-0.5 h-4 w-4 shrink-0',
          tone === 'bad' && 'text-destructive',
          tone === 'ok' && 'text-success',
          tone === 'info' && 'text-muted-foreground'
        )}
      />
      <div className="min-w-0">
        <p className="font-semibold">{title}</p>
        {children && <div className="mt-0.5 text-muted-foreground">{children}</div>}
      </div>
    </div>
  );
}

/** Inline field error. */
export function FieldError({ id, children }: { id?: string; children?: React.ReactNode }) {
  if (!children) return null;
  return (
    <p id={id} className="text-[12.5px] text-destructive">
      {children}
    </p>
  );
}

/** Password input with reveal toggle. */
export const PasswordInput = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, ...props }, ref) => {
    const [show, setShow] = React.useState(false);
    return (
      <div className="relative">
        <Input
          ref={ref}
          type={show ? 'text' : 'password'}
          className={cn('h-11 pr-11', className)}
          {...props}
        />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="absolute right-1 top-1/2 h-8 w-8 -translate-y-1/2 text-muted-foreground"
          aria-label={show ? 'Hide password' : 'Show password'}
          aria-pressed={show}
          onClick={() => setShow((s) => !s)}
        >
          {show ? <EyeOff /> : <Eye />}
        </Button>
      </div>
    );
  }
);
PasswordInput.displayName = 'PasswordInput';

/** Live password requirements checklist. */
export function PasswordRules({ value, id }: { value: string; id?: string }) {
  return (
    <ul
      id={id}
      aria-label="Password requirements"
      className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1.5 max-[400px]:grid-cols-1"
    >
      {PASSWORD_RULES.map((r) => {
        const met = r.test(value);
        return (
          <li
            key={r.key}
            className={cn(
              'flex items-center gap-1.5 text-[12.5px]',
              met ? 'text-success' : 'text-muted-foreground'
            )}
          >
            {met ? <Check className="h-3.5 w-3.5" /> : <Circle className="h-3.5 w-3.5" />}
            <span>{r.label}</span>
            <span className="sr-only">{met ? '(met)' : '(not met)'}</span>
          </li>
        );
      })}
    </ul>
  );
}

/** Six single-digit inputs: auto-advance, backspace, arrows, paste. */
export function OtpInput({
  value,
  onChange,
  onComplete,
  invalid,
  label = 'Verification code',
  labelledBy,
  length = 6,
  autoFocus,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  onComplete?: (v: string) => void;
  invalid?: boolean;
  label?: string;
  labelledBy?: string;
  length?: number;
  autoFocus?: boolean;
  disabled?: boolean;
}) {
  const refs = React.useRef<(HTMLInputElement | null)[]>([]);
  const digits = Array.from({ length }, (_, i) => value[i] ?? '');

  React.useEffect(() => {
    if (autoFocus) refs.current[0]?.focus();
  }, [autoFocus]);

  const commit = (next: string[]) => {
    const v = next.join('').slice(0, length);
    onChange(v);
    if (v.length === length && next.every(Boolean)) onComplete?.(v);
  };

  return (
    <div
      role="group"
      aria-label={labelledBy ? undefined : label}
      aria-labelledby={labelledBy}
      className="flex justify-between gap-2 max-[400px]:gap-1.5"
    >
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => {
            refs.current[i] = el;
          }}
          value={d}
          inputMode="numeric"
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          maxLength={1}
          pattern="[0-9]"
          disabled={disabled}
          aria-label={`Digit ${i + 1} of ${length}`}
          aria-invalid={invalid || undefined}
          className="h-12 w-11 min-w-0 rounded-md border border-input bg-card text-center font-mono text-lg font-semibold tabular-nums hover:border-foreground/30 focus-visible:border-primary focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-primary/20 disabled:opacity-60 aria-[invalid=true]:border-destructive max-[400px]:w-full"
          onFocus={(e) => e.currentTarget.select()}
          onChange={(e) => {
            const ch = e.target.value.replace(/\D/g, '').slice(-1);
            const next = [...digits];
            next[i] = ch;
            commit(next);
            if (ch && i < length - 1) refs.current[i + 1]?.focus();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Backspace' && !digits[i] && i > 0) {
              e.preventDefault();
              const next = [...digits];
              next[i - 1] = '';
              commit(next);
              refs.current[i - 1]?.focus();
            }
            if (e.key === 'ArrowLeft' && i > 0) refs.current[i - 1]?.focus();
            if (e.key === 'ArrowRight' && i < length - 1) refs.current[i + 1]?.focus();
          }}
          onPaste={(e) => {
            const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
            if (!pasted) return;
            e.preventDefault();
            commit(Array.from({ length }, (_, j) => pasted[j] ?? ''));
            refs.current[Math.min(pasted.length, length - 1)]?.focus();
          }}
        />
      ))}
    </div>
  );
}

/** Submit button with busy state. */
export function BusyButton({
  busy,
  busyLabel,
  children,
  disabled,
  ...props
}: ButtonProps & { busy?: boolean; busyLabel: string }) {
  return (
    <Button size="lg" className="w-full" disabled={disabled || busy} aria-busy={busy} {...props}>
      {busy ? (
        <>
          <Loader2 className="animate-spin" />
          <span>{busyLabel}</span>
        </>
      ) : (
        children
      )}
    </Button>
  );
}

/** Seconds countdown; start(n) begins, returns remaining. */
export function useCountdown() {
  const [left, setLeft] = React.useState(0);
  React.useEffect(() => {
    if (left <= 0) return;
    const t = window.setTimeout(() => setLeft((l) => l - 1), 1000);
    return () => window.clearTimeout(t);
  }, [left]);
  return { left, start: (s: number) => setLeft(s), stop: () => setLeft(0) };
}

/** Mono email chip. */
export function EmailChip({ children }: { children: React.ReactNode }) {
  return (
    <span className="break-words font-mono text-[13px] text-foreground [overflow-wrap:anywhere]">
      {children}
    </span>
  );
}

/** 3-segment progress bar (reset flow). */
export function StepProgress({ step, total = 3 }: { step: number; total?: number }) {
  return (
    <div className="flex flex-col">
      <p className="mb-2.5 font-mono text-[11px] uppercase tracking-[0.08em] text-muted-foreground">
        Step {step} of {total}
      </p>
      <div
        className="grid gap-1.5"
        style={{ gridTemplateColumns: `repeat(${total}, minmax(0, 1fr))` }}
        aria-hidden="true"
      >
        {Array.from({ length: total }, (_, i) => (
          <span
            key={i}
            className={cn('h-[3px] rounded-full', i < step ? 'bg-foreground' : 'bg-border')}
          />
        ))}
      </div>
    </div>
  );
}

export const linkCls = 'font-medium text-foreground underline-offset-4 hover:underline';
export const backCls =
  'inline-flex items-center gap-1.5 self-start text-[13px] text-muted-foreground hover:text-foreground [&_svg]:h-[15px] [&_svg]:w-[15px]';

/** Inline email echo inside a sentence. */
export function EmailEcho({ children }: { children: React.ReactNode }) {
  return <EmailChip>{children}</EmailChip>;
}
